import { useState, useEffect, useRef } from 'react';
import { Input, Label } from '@/components/ui';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface AutocompleteOption {
  value: string;
  label: string;
  subtitle?: string;
}

interface AutocompleteProps {
  value: string[];
  onChange: (value: string[]) => void;
  onSearch: (query: string) => Promise<AutocompleteOption[]>;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  minChars?: number;
}

export function Autocomplete({
  value,
  onChange,
  onSearch,
  placeholder,
  label,
  disabled,
  className,
  minChars = 2,
}: AutocompleteProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState<AutocompleteOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search with debounce
  useEffect(() => {
    if (search.length < minChars) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await onSearch(search);
        setSuggestions(results);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Autocomplete search error:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search, onSearch, minChars]);

  const handleAdd = (option: AutocompleteOption) => {
    if (!value.includes(option.value)) {
      onChange([...value, option.value]);
    }
    setSearch('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleRemove = (optionValue: string) => {
    onChange(value.filter(v => v !== optionValue));
  };

  // Get labels for selected values
  const selectedLabels = value.map(v => {
    const option = suggestions.find(s => s.value === v);
    return option?.label || v;
  });

  return (
    <div className={className}>
      {label && <Label>{label}</Label>}

      {/* Selected items as badges */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((v, index) => (
            <div
              key={v}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-primary/10 text-primary border border-primary/20"
            >
              <span>{selectedLabels[index]}</span>
              <button
                type="button"
                onClick={() => handleRemove(v)}
                disabled={disabled}
                className="hover:bg-primary/20 rounded p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search input */}
      <div ref={wrapperRef} className="relative">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder || t('common.search')}
          disabled={disabled}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
        />

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
            {loading && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {t('common.loading')}...
              </div>
            )}
            {!loading && suggestions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleAdd(option)}
                disabled={value.includes(option.value)}
                className="w-full px-3 py-2 text-left hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="text-sm text-foreground">{option.label}</div>
                {option.subtitle && (
                  <div className="text-xs text-muted-foreground">{option.subtitle}</div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* No results message */}
        {showSuggestions && !loading && search.length >= minChars && suggestions.length === 0 && (
          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg">
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t('common.noResults')}
            </div>
          </div>
        )}
      </div>

      {/* Helper text */}
      {search.length > 0 && search.length < minChars && (
        <p className="text-xs text-muted-foreground mt-1">
          {t('common.typeAtLeastChars', { count: minChars })}
        </p>
      )}
    </div>
  );
}
